import { useMemo, useState } from "react"

import { useSyncLogs } from "@/api/hooks/sync"
import { useCancelSyncJob } from "@/api/hooks/admin"
import {
  formatDisplayDateTime,
  rewriteIsoDatesInText,
} from "@/lib/dates"
import { SyncModal } from "@/pages/admin/sync/SyncTab"

function showToast(msg: string, type: "success" | "error" = "success") {
  const el = document.createElement("div")
  el.textContent = msg
  el.className = `fixed top-4 right-4 z-[9999] rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
    type === "error"
      ? "bg-red-900/90 text-red-200 border border-red-700"
      : "bg-green-900/90 text-green-200 border border-green-700"
  }`
  document.body.appendChild(el)
  setTimeout(() => el.remove(), 3000)
}

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-[#154c2f] text-[#4ade80] border-green-700",
  running: "bg-[#2c3e50] text-[#8b9aff] border-blue-700",
  queued: "bg-[#3a3420] text-[#fbbf24] border-amber-700",
  timeout: "bg-[#4c3420] text-[#fbbf24] border-amber-700",
  failure: "bg-[#4c1f1f] text-[#f87171] border-red-700",
  failed: "bg-[#4c1f1f] text-[#f87171] border-red-700",
}

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? "bg-[#555] text-white"
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  )
}

export default function AdminLogsTab() {
  const [syncOpen, setSyncOpen] = useState(false)
  // Single fetch — do not also call useLastSyncInfo (duplicate /logs?limit=1).
  const { data, isLoading, error, refetch } = useSyncLogs(50)
  const cancelMut = useCancelSyncJob()

  const logs = data?.data ?? []

  const lastSyncText = useMemo(() => {
    const row = logs[0]
    if (!row) return "No sync history"
    if (!row.started_at) return "Unknown"
    return `${formatDisplayDateTime(new Date(row.started_at))} — ${row.status}`
  }, [logs])

  async function handleCancel(logId: number) {
    try {
      await cancelMut.mutateAsync(logId)
      showToast("Job cancelled")
      refetch()
    } catch {
      showToast("Failed to cancel job", "error")
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="border-b-2 border-[#8b9aff] pb-2 text-2xl font-semibold text-white">
            Sync Logs
          </h2>
          <p className="mt-1 text-xs text-white/70">Last Sync: {lastSyncText}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-white">
            {isLoading
              ? "Loading..."
              : error
                ? "Error loading logs"
                : `${logs.length} log entries`}
          </span>
          <button
            onClick={() => refetch()}
            className="rounded-md border border-[#676d78] bg-[#4f5561] px-3 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#5a616f]"
          >
            🔄 Refresh
          </button>
          <button
            type="button"
            onClick={() => setSyncOpen(true)}
            className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-3 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
          >
            🔄 Sync Data
          </button>
        </div>
      </div>

      <SyncModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        onStarted={() => {
          void refetch()
        }}
      />

      <div className="overflow-x-auto rounded-lg border border-[#4a4a4a]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#444444] text-white">
            <tr>
              <th className="px-4 py-3 font-medium">Time</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Details</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#4a4a4a]">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-white">
                  Loading logs...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-red-400">
                  Failed to load logs.
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-white">
                  No log entries found.
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="transition-colors hover:bg-[#444444]">
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-white">
                    {log.started_at
                      ? formatDisplayDateTime(new Date(log.started_at))
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-white">{log.job_type}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={log.status} />
                  </td>
                  <td className="max-w-[560px] px-4 py-3 align-top">
                    <div className="max-h-56 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-white">
                      {log.details
                        ? rewriteIsoDatesInText(log.details)
                        : "—"}
                      {log.completed_at && (
                        <div className="mt-2 font-sans text-xs text-[#aaa]">
                          Completed:{" "}
                          {formatDisplayDateTime(new Date(log.completed_at))}
                          {log.duration_seconds != null &&
                            ` (${Number(log.duration_seconds).toFixed(1)}s)`}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {(log.status === "running" || log.status === "queued") && (
                      <button
                        onClick={() => handleCancel(log.id)}
                        disabled={cancelMut.isPending}
                        className="rounded-md border border-[#d26a78]/35 bg-[#c6596a] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#b84f60]"
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
